'use client';

import {useState, useEffect} from 'react';
import {useLocale} from 'next-intl';
import Card from '@/components/ui/Card';

interface Temoignage {
  id: number;
  projet_id: number;
  nom_beneficiaire: string;
  texte_fr: string;
  texte_ar?: string;
  photo_url?: string;
  video_url?: string;
  note?: number;
  gouvernorat?: string;
  created_at?: string;
}

export default function TemoignagesPage() {
  const locale = useLocale();
  const [temoignages, setTemoignages] = useState<Temoignage[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{total_approuves: number; note_moyenne: number | null}>({total_approuves: 0, note_moyenne: null});

  useEffect(() => {
    Promise.all([
      fetch('/api/v1/temoignages/?statut=approuve&limit=50').then(r => r.ok ? r.json() : []),
      fetch('/api/v1/temoignages/stats').then(r => r.ok ? r.json() : {total_approuves: 0, note_moyenne: null}),
    ]).then(([data, s]) => {
      setTemoignages(data);
      setStats(s);
    }).finally(() => setLoading(false));
  }, []);

  const renderStars = (note: number) => {
    return Array.from({length: 5}, (_, i) => (
      <svg key={i} className={`w-4 h-4 ${i < note ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-primary mb-3">
          {locale === 'ar' ? 'شهادات المستفيدين' : locale === 'en' ? 'Beneficiary Testimonials' : 'Temoignages des beneficiaires'}
        </h1>
        <p className="text-muted max-w-2xl mx-auto">
          {locale === 'ar'
            ? 'قصص نجاح حقيقية من المستفيدين من مشاريع الاتحاد التونسي للتضامن الاجتماعي'
            : locale === 'en'
            ? 'Real success stories from beneficiaries of UTSS solidarity projects'
            : 'Des histoires vraies de reussite de beneficiaires des projets solidaires de l\'UTSS'}
        </p>
        {stats.total_approuves > 0 && (
          <div className="flex items-center justify-center gap-6 mt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{stats.total_approuves}</div>
              <div className="text-xs text-muted">{locale === 'ar' ? 'شهادة' : 'temoignages'}</div>
            </div>
            {stats.note_moyenne && (
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-500">{stats.note_moyenne}/5</div>
                <div className="text-xs text-muted">{locale === 'ar' ? 'التقييم المتوسط' : 'note moyenne'}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted">Chargement...</div>
      ) : temoignages.length === 0 ? (
        <div className="text-center py-16 text-muted">
          <svg className="w-16 h-16 mx-auto mb-4 text-muted/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p>Aucun témoignage pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {temoignages.map(t => (
            <Card key={t.id} className="relative overflow-hidden">
              {/* Quote icon */}
              <div className="absolute top-4 right-4 text-primary/10">
                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>

              <div className="flex gap-4">
                {t.photo_url ? (
                  <img src={t.photo_url} alt={t.nom_beneficiaire} className="w-16 h-16 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-2xl font-bold text-primary">{t.nom_beneficiaire.charAt(0)}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground">{t.nom_beneficiaire}</h3>
                  {t.gouvernorat && <span className="text-xs text-muted">{t.gouvernorat}</span>}
                  {t.note && <div className="flex gap-0.5 mt-1">{renderStars(t.note)}</div>}
                </div>
              </div>

              <blockquote className="mt-4 text-sm text-foreground/80 leading-relaxed italic">
                &laquo; {locale === 'ar' && t.texte_ar ? t.texte_ar : t.texte_fr} &raquo;
              </blockquote>

              {t.video_url && (
                <a href={t.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs text-primary hover:underline">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  Voir la video
                </a>
              )}

              <div className="mt-3 pt-3 border-t border-border">
                <a href={`/${locale}/projets/${t.projet_id}`} className="text-xs text-primary hover:underline">
                  Voir le projet associe →
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
